<script setup>
import { ref, onMounted, reactive, computed } from "vue";
import { useTheme } from "vuetify";
import { appBarStore } from "@/stores/appBar";
import { apiQuery } from "@/api/j24api";
import AlertsList from "@/components/AlertsList.vue";
import AppIcon from "./components/AppIcon.vue";
import AddCommand from "./components/AddCommand.vue";
import { patch24Store } from "@/stores/patch24";
import { storeToRefs } from "pinia";
import CommandsList from "./components/CommandsList.vue";
import { permissionsStore } from "@/stores/permissions";
const permissions = permissionsStore();

const patch24 = patch24Store();
const { selectedLicense, commands, appsList, commandsUpdated } =
  storeToRefs(patch24);

const appBar = appBarStore();
const theme = useTheme();

const loading = ref(false);
const alerts = ref([]);
//const deviceCommands = reactive([]);

const devices = reactive([]);

onMounted(async () => {
  theme.global.name.value = "jigsawNew";
  if (commands.value.length === 0 && appBar.selected) {
    await getData();
  } else if (appBar.selected && commandsUpdated.value) {
    const fifteenMinutesAgo = new Date(Date.now() - 15 * 60 * 1000);
    if (commandsUpdated.value < fifteenMinutesAgo) {
      await getData();
    }
  }
});

appBar.$subscribe(() => {
  //console.log("Appbar changed", appBar.$state);
  if (appBar.selected) {
    getData();
  }
});

const readOnlyMode = computed(() => {
  let result =
    permissions.$state &&
    permissions.$state["PatchingV2-Commands"] &&
    permissions.$state["PatchingV2-Commands"].actions.includes("Update");
  return !result;
});

async function getData() {
  commands.value.length = 0;
  loading.value = true;
  let promises = [];
  promises.push(getLicense(appBar.selected));
  promises.push(getDeviceCommands());
  promises.push(getAppsList());
  await Promise.all(promises);
  if (selectedLicense.value.GlobalConfiguration?.Commands?.length) {
    selectedLicense.value.GlobalConfiguration.Commands.forEach((command) => {
      if (command.status == "cancelled") {
        return;
      }
      command.global = true;
      commands.value.push({ ...command, scope: "Global" });
    });
  }

  if (selectedLicense.value.Configurations?.length) {
    selectedLicense.value.Configurations.forEach((config) => {
      config.groupID = config.ConfigurationID;
      if (config.Commands?.length) {
        config.Commands = config.Commands.filter(
          (c) => c.status != "cancelled"
        );
        config.Commands.forEach((command) => {
          commands.value.push({
            ...command,
            //scope: "Configuration " + config.groupID,
          });
        });
      }
    });
  }
  loading.value = false;
  commandsUpdated.value = new Date();
  getDevices();
}

async function getDevices() {
  try {
    devices.length = 0;
    let result = await apiQuery("ELA", "patching/patchingManagement", {
      action: "getDevices",
      id: appBar.selected,
      minimal: true,
    });
    if (result.length) {
      devices.push(...result);
    } else {
      alerts.value.push({
        type: "error",
        message: "Error getting devices",
        closable: true,
      });
    }
  } catch (error) {
    alerts.value.push({
      type: "error",
      message: "Error getting devices",
      closable: true,
    });
  }
}
function getAppName(appId) {
  const app = appsList.find((app) => app.id === appId);
  return app ? app.name : "Unknown App";
}

async function getAppsList() {
  try {
    const result = await apiQuery("ELA", "patching/patchingManagement", {
      action: "getAppsList",
      licenseId: appBar.selected,
    });
    if (result.length) {
      appsList.value.length = 0;
      appsList.value.push(...result);
    } else {
      alerts.value.push({
        type: "error",
        message: "Error getting apps",
        closable: true,
      });
      console.log("Empty apps list returned");
    }
  } catch (error) {
    alerts.value.push({
      type: "error",
      message: "Error getting apps",
      closable: true,
    });
    console.log("Error getting apps x", error);
  }
}

async function getDeviceCommands() {
  var deviceCommands = [];
  try {
    const result = await apiQuery("ELA", "patching/patchingManagement", {
      action: "getDeviceCommands",
      licenseId: appBar.selected,
    });
    if (result.result.length) {
      deviceCommands.push(...result.result);
    }
    commands.value.push(...deviceCommands);
  } catch (error) {
    alerts.value.push({
      type: "error",
      message: "Error fetching device commands.",
      closable: true,
    });
  }
}

async function getLicense(id) {
  try {
    var result = await apiQuery("ELA", "patching/patchingManagement", {
      action: "getPatchingLicense",
      id: id,
    });
    if (result.length) {
      selectedLicense.value = result[0];
    }
  } catch (error) {
    alerts.value.push({
      type: "error",
      message: "Error getting license",
      closable: true,
    });
  }
}

const showConfirmDeleteAll = ref(false);

function clearAllPressed(item) {
  showConfirmDeleteAll.value = true;
}

async function deleteAllCommands() {
  loading.value = true;
  showConfirmDeleteAll.value = false;
  for (const item of commands.value) {
    try {
      console.log("Sending cancel for command", item);
      var body = {
        action: "cancelCommand",
        licenseId: patch24.selectedLicense._id,
      };
      if (item.serial) {
        body.id = item._id;
        body.serial = item.serial;
      } else if (item.scope == "Global") {
        body.uuid = item.uuid;
      } else if (item.groupID) {
        body.groupID = item.groupID;
        body.uuid = item.uuid;
      }
      let result = await apiQuery("ELA", "patching/patchingManagement", body);
      console.log("Sending cancel for command", body);
      console.log("Result", result);
      // if (result.message == "Command cancelled successfully") {
      //   alerts.value.push({
      //     type: "success",
      //     message: "Command cancelled successfully",
      //     closable: true,
      //     timeout: 5,
      //   });
      // }
    } catch (error) {
      console.error("Error cancelling command:", error);
      alerts.value.push({
        type: "error",
        message: "Error cancelling command",
        closable: true,
      });
    }
  }
  getData();
}
</script>

<template>
  <!-- Delete/Cancel Command Overlay -->
  <v-dialog v-model="showConfirmDeleteAll" max-width="400">
    <v-card>
      <v-card-title class="headline">Cancel All</v-card-title>
      <v-card-text> Are you sure you want to cancel all commands? </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="elevated" @click="showConfirmDeleteAll = false"
          >Cancel</v-btn
        >
        <v-btn color="primary" variant="elevated" @click="deleteAllCommands"
          >Delete</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>

  <template v-if="appBar.selected">
    <AlertsList v-model="alerts" />

    <v-row class="mb-0">
      <v-col class="ml-1">
        <h1>Command Viewer</h1>
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="auto">
        <v-btn @click="getData()"><v-icon>mdi-refresh</v-icon></v-btn>
      </v-col>
      <v-col cols="auto" class="mr-1" v-if="!readOnlyMode">
        <v-btn @click="clearAllPressed()" color="primary">Delete All</v-btn>
      </v-col>
    </v-row>
    <v-sheet rounded="lg" elevation="1" class="ma-1">
      <CommandsList
        :commands="commands"
        :loading="loading"
        :appsList="appsList"
        :readOnlyMode="readOnlyMode"
      />
    </v-sheet>
  </template>
  <template v-else>
    <v-row>
      <v-col>
        <h1 v-if="appBar.items.length > 1">Select Patching License Above</h1>
        <h1 v-else>Loading please wait...</h1>
      </v-col>
    </v-row>
  </template>
</template>
